import { useCallback, useEffect, useState } from "react";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Icon from "../../common/elements/Icon";
import Button from "../../common/elements/Button";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import InfoCard from "../../common/elements/InfoCard";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { useThor } from "../context/ThorContext";
import { serverErrMsg } from "../utils/validators";

// ── Add override modal ─────────────────────────────────────────────────────

const AddOverrideModal = ({ isActive, buckets, isSubmitting, onClose, onSubmit }) => {
    const [userId, setUserId] = useState('');
    const [bucketId, setBucketId] = useState('');

    useEffect(() => {
        if (isActive) { setUserId(''); setBucketId(buckets[0]?.id ?? ''); }
    }, [isActive, buckets]);

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card">
                <header className="modal-card-head">
                    <p className="modal-card-title">Add Override</p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    <div className="field">
                        <label className="label is-small">
                            User ID <span className="has-text-danger">*</span>
                            <HelpTooltip text={HELP.overrides.userId} />
                        </label>
                        <div className="control">
                            <input className="input is-small" type="text" value={userId}
                                   onChange={e => setUserId(e.target.value)}
                                   placeholder="e.g. member:12345678 or tripadvisor.com:user@email.com" />
                        </div>
                    </div>
                    <div className="field">
                        <label className="label is-small">Bucket <span className="has-text-danger">*</span></label>
                        <div className="control">
                            <div className="select is-small is-fullwidth">
                                <select value={bucketId} onChange={e => setBucketId(Number(e.target.value))}>
                                    {buckets.map(b => (
                                        <option key={b.id} value={b.id}>
                                            {b.key}{b.control ? ' (control)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </section>
                <footer className="modal-card-foot">
                    <Button className="is-link mr-2"
                            disabled={isSubmitting || !userId.trim() || !bucketId}
                            onClick={() => onSubmit({ userId: userId.trim(), bucketId: Number(bucketId) })}>
                        {isSubmitting ? 'Adding…' : 'Add Override'}
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────

const OverridesTab = ({ experimentKey }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();

    const [overridesState, setOverridesState] = useState(buildLoadingState([]));
    const [bucketsState, setBucketsState] = useState(buildLoadingState([]));
    const [skipAudiences, setSkipAudiences] = useState(false);
    const [isTogglingSkip, setIsTogglingSkip] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const fetchOverrides = useCallback(() => {
        setOverridesState(buildLoadingState([]));
        Promise.all([
            execute('GET', thor_api_urls.forcedBuckets(experimentKey)),
            execute('GET', thor_api_urls.experiment(experimentKey)).catch(() => null),
        ]).then(([data, expData]) => {
                setOverridesState(buildDataState(data?.forcedBuckets ?? []));
                const detail = expData?.experiment ?? expData;
                setSkipAudiences(detail?.forcedBucketsSkipAudience ?? false);
            })
            .catch(err => {
                console.error("Failed to fetch overrides:", err);
                setOverridesState(buildErrorState(err, []));
            });
    }, [execute, experimentKey]);

    const fetchBuckets = useCallback(() => {
        execute('GET', thor_api_urls.experimentBuckets(experimentKey))
            .then(data => setBucketsState(buildDataState(data?.buckets ?? data ?? [])))
            .catch(err => setBucketsState(buildErrorState(err, [])));
    }, [execute, experimentKey]);

    useEffect(() => { fetchOverrides(); fetchBuckets(); }, [fetchOverrides, fetchBuckets]);

    const handleToggleSkip = (newVal) => {
        if (!editMode) return;
        if (newVal === true) {
            const confirmed = window.confirm(
                'Enable skip-audience mode? Overridden users will bypass all audience eligibility checks. This may affect QA testing fidelity. Are you sure?'
            );
            if (!confirmed) return;
        }
        setIsTogglingSkip(true);
        execute('POST', thor_api_urls.forcedBucketsSkipAudience(experimentKey, newVal))
            .then(() => {
                setSkipAudiences(newVal);
                addToast(`Skip audiences ${newVal ? 'enabled' : 'disabled'}.`, 'success');
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to update skip audiences setting.'), 'danger'))
            .finally(() => setIsTogglingSkip(false));
    };

    const handleSubmit = ({ userId, bucketId }) => {
        setIsSubmitting(true);
        execute('POST', thor_api_urls.forcedBuckets(experimentKey), { userId, bucketId })
            .then(() => {
                setModalOpen(false);
                fetchOverrides();
                addToast(`Override added for "${userId}".`, 'success');
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to add override.'), 'danger'))
            .finally(() => setIsSubmitting(false));
    };

    const handleDelete = (override) => {
        setDeletingId(override.userId);
        execute('DELETE', thor_api_urls.forcedBucket(experimentKey, override.userId))
            .then(() => {
                fetchOverrides();
                addToast(`Override removed for "${override.userId}".`, 'success');
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to remove override.'), 'danger'))
            .finally(() => setDeletingId(null));
    };

    const overrides = overridesState.data ?? [];
    const buckets = bucketsState.data ?? [];

    return (
        <>
            <div className="level mb-3">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">
                        Overrides<HelpTooltip text={HELP.overrides.general} />
                    </p>
                </div>
                <div className="level-right">
                    <Button className="is-link is-small" disabled={!editMode || buckets.length === 0}
                            onClick={() => setModalOpen(true)}>
                        <Icon icon={faPlus} /><span>Add Override</span>
                    </Button>
                </div>
            </div>

            <div className="notification is-light is-size-7 mb-4">
                <strong>Tip:</strong> Adding overrides here is primarily for bulk use. For individual QA testing, prefer a browser cookie, a bookmarklet, or the /ThorHammer tool — see Tripdocs for details.
            </div>

            {/* Skip audiences toggle */}
            <div className="field mb-4">
                <label className="label is-small">
                    Skip Audiences Eligibility Checks<HelpTooltip text={HELP.overrides.skipAudiences} />
                </label>
                <div className="buttons has-addons">
                    <button type="button"
                        className={`button is-small ${skipAudiences === true ? 'is-warning is-selected' : ''}`}
                        disabled={!editMode || isTogglingSkip}
                        onClick={() => handleToggleSkip(true)}>Yes</button>
                    <button type="button"
                        className={`button is-small is-light has-text-grey ${skipAudiences === false ? 'is-selected' : ''}`}
                        disabled={!editMode || isTogglingSkip}
                        onClick={() => handleToggleSkip(false)}>No</button>
                </div>
            </div>

            <StateHandler showLoader={overridesState.loading} showError={overridesState.error}
                          loadingScreen={<SmallLoadingSpinner />}>
                {overrides.length === 0
                    ? <InfoCard title="No overrides set for this experiment." />
                    : (
                        <table className="table is-fullwidth is-striped is-hoverable">
                            <thead>
                                <tr>
                                    <th>User ID</th>
                                    <th>Bucket</th>
                                    <th className="has-text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {overrides.map(o => (
                                    <tr key={o.userId}>
                                        <td className="is-family-monospace" style={{ fontSize: '0.85rem' }}>{o.userId}</td>
                                        <td>
                                            <span className="tag is-info is-light">{o.bucketKey}</span>
                                        </td>
                                        <td className="has-text-right">
                                            <button type="button"
                                                className="button is-small is-ghost has-text-danger"
                                                aria-label="Remove override"
                                                style={{ padding: '0 0.25rem' }}
                                                disabled={!editMode || deletingId === o.userId}
                                                onClick={editMode ? () => handleDelete(o) : undefined}>
                                                <Icon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </StateHandler>

            <AddOverrideModal isActive={modalOpen} buckets={buckets} isSubmitting={isSubmitting}
                              onClose={() => setModalOpen(false)} onSubmit={handleSubmit} />
        </>
    );
};

export default OverridesTab;
